import React, { Dispatch, SetStateAction, useCallback } from "react";

import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import { debounce } from "lodash";
import { useCompaniesTableStore } from "@/stores/useTableStore";

import useAxiosPrivate from "../../../../../../../api/hooks/useAxiosPrivate";
import { ApiResources } from "../../../../../../../api/resources";
import { ALERT_STATUS } from "../../../../../../../context/alert/types";
import useResponse from "../../../../../../../shared/hooks/useResponse";
import { COMPANY_SETTINGS } from "../../../../../types";

interface UseHandleToggleSettingsProps {
  detailsData: any;
}

const useHandleToggleSettings = ({
  detailsData
}: UseHandleToggleSettingsProps) => {
  const { postData, putData } = useAxiosPrivate();
  const { handleCommonError, handleResponse } = useResponse();
  const { t } = useTranslation();
  const location = useLocation();

  const { setDetailsData } = useCompaniesTableStore();

  const updateDetailsData = useCallback(
    (
      propertyName: string | COMPANY_SETTINGS,
      newValue: boolean | string,
      overdraftValue?: string
    ) => {
      const updatedData = overdraftValue
        ? {
            ...detailsData,
            [propertyName]: newValue,
            [COMPANY_SETTINGS.OverDraftAmount]: overdraftValue
          }
        : { ...detailsData, [propertyName]: newValue };

      setDetailsData(updatedData);
    },
    [detailsData, location.pathname]
  );

  // debounce simplifies the logic and ensures that the API call is made only after the user has stopped toggling for a short period (300ms in here)
  const debouncedUpdateDetailsData = debounce(updateDetailsData, 300);

  const handleToggleCreditsOverdraft = (
    event: React.ChangeEvent<HTMLInputElement>,
    setIsCreditsOverdraftChecked: Dispatch<SetStateAction<boolean>>,
    overdraftInputValue: string,
    setOverdraftInputValue: Dispatch<SetStateAction<string>>
  ) => {
    const initialOverdraftAmount = 0;
    const newState = event.target.checked;
    const creditsAmount =
      newState === true ? +overdraftInputValue : initialOverdraftAmount;
    const payload = {
      companyId: detailsData?.id,
      Amount: creditsAmount
    };

    const revertState = () => {
      setIsCreditsOverdraftChecked(!newState);
      updateDetailsData(
        COMPANY_SETTINGS.OverDraftEnabled,
        !newState,
        `${initialOverdraftAmount}`
      ); // Revert the state in the detailsData and reset overdraft amount to 0
    };
    if (+overdraftInputValue !== initialOverdraftAmount) {
      setIsCreditsOverdraftChecked(newState);
      setOverdraftInputValue(`${creditsAmount}`);
      // Debounced update
      debouncedUpdateDetailsData(
        COMPANY_SETTINGS.OverDraftEnabled,
        newState,
        `${creditsAmount}`
      );

      callApiToToggleCreditsOverdraft(payload, revertState);
    }
  };

  const callApiToToggleCreditsOverdraft = useCallback(
    async (
      payload: { companyId: any; Amount: number },
      revertState: () => void
    ) => {
      try {
        await postData(ApiResources.CreditsLoan, payload);
        const successMessage =
          payload.Amount > 0
            ? t("Alert##credits overdraft activated")
            : t("Alert##credits overdraft deactivated");

        handleResponse(ALERT_STATUS.Success, successMessage);
      } catch (error) {
        handleCommonError(error, t);
        revertState();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleCommonError, postData]
  );

  const handleToggleCompanySettings = (
    event: React.ChangeEvent<HTMLInputElement>,
    settingName: COMPANY_SETTINGS,
    setNewState: Dispatch<SetStateAction<boolean>>
  ) => {
    const newState = event.target.checked;

    const revertState = () => {
      setNewState(!newState);
      updateDetailsData(settingName, !newState); // Revert the state in the detailsData
    };

    setNewState(newState);
    // Debounced update
    debouncedUpdateDetailsData(settingName, newState); // Update the specific setting in the detailsData, so that

    callApiToToggleCompanySettings(
      {
        id: detailsData?.id,
        [settingName]: newState
      },
      revertState
    );
  };

  const callApiToToggleCompanySettings = useCallback(
    async (payload: Object, revertState: () => void) => {
      try {
        await putData(ApiResources.CompaniesSettings, payload);
      } catch (error) {
        handleCommonError(error, t);
        revertState(); // Revert the state if the API call fails
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleCommonError, putData]
  );

  return { handleToggleCompanySettings, handleToggleCreditsOverdraft };
};

export default useHandleToggleSettings;
